/**
 * Money primitive shared across the apps. Centralised so "how money is stored,
 * validated, and rendered" lives in one place instead of inline cents math and
 * a lone `formatMinor` copy in the tax card.
 *
 * Convention (matches the tax engine and billing): a money value is an integer
 * in the currency's *minor units* plus an ISO-4217 currency code. Integers avoid
 * binary-fraction drift and match what Stripe / channel APIs speak. The minor
 * unit is currency-dependent (EUR/USD = cents = 2 digits, JPY = 0, BHD = 3), so
 * the conversion helpers derive the exponent from the currency rather than
 * hard-coding `/100`.
 */

import countryToCurrency from "country-to-currency";
// Type-only, so this module stays zod-free for the browser surfaces that render
// prices. `money-schema.ts` owns the validation half; see its docblock.
import type { CurrencyCode, Money } from "./money-schema";
import { parseTypedDecimal } from "./typed-decimal";

/** Ultimate fallback when no property/reservation currency is known. */
export const DEFAULT_CURRENCY = "EUR";

/**
 * ISO-4217 codes the runtime knows about, via ICU. Empty on runtimes without
 * `Intl.supportedValuesOf` (then validation falls back to a well-formedness
 * check).
 */
const CURRENCY_CODES: readonly string[] = (() => {
  try {
    return Object.freeze([...Intl.supportedValuesOf("currency")]);
  } catch {
    return Object.freeze([]);
  }
})();

const ISO_4217_SET: ReadonlySet<string> = new Set(CURRENCY_CODES);
const WELL_FORMED_CURRENCY_CODE_RE = /^[A-Z]{3}$/u;

/** True if `code` is a known ISO-4217 code (or, on older runtimes, well-formed). */
export function isCurrencyCode(code: string): boolean {
  const upper = code.toUpperCase();
  return ISO_4217_SET.size > 0
    ? ISO_4217_SET.has(upper)
    : WELL_FORMED_CURRENCY_CODE_RE.test(upper);
}

const fractionDigitsByCurrency = new Map<string, number>();

/**
 * Number of fractional digits the currency uses (its minor-unit exponent):
 * 2 for EUR/USD, 0 for JPY, 3 for BHD. Falls back to 2 for unknown codes.
 */
export function currencyFractionDigits(currency: string): number {
  const code = currency.toUpperCase();
  const cached = fractionDigitsByCurrency.get(code);
  if (cached !== undefined) {
    return cached;
  }
  // Only a code Intl accepts is cached: the guest pay page passes its query
  // string here, so caching every input would grow without bound.
  try {
    const digits =
      new Intl.NumberFormat("en", {
        currency: code,
        style: "currency",
      }).resolvedOptions().maximumFractionDigits ?? 2;
    fractionDigitsByCurrency.set(code, digits);
    return digits;
  } catch {
    return 2;
  }
}

/** Minor units -> major units (e.g. 1250 EUR-cents -> 12.5). */
export function minorToMajor(amountMinor: number, currency: string): number {
  return amountMinor / 10 ** currencyFractionDigits(currency);
}

/** Major units -> integer minor units, rounded (e.g. 12.5 EUR -> 1250). */
export function majorToMinor(amountMajor: number, currency: string): number {
  return Math.round(amountMajor * 10 ** currencyFractionDigits(currency));
}

/**
 * A price a host typed, in minor units, or null when the field is empty or not
 * an amount of 0 or more in the currency's own decimals ("12,50" and "12.50"
 * both read 1250 cents; "1,250" in EUR is refused). See `parseTypedDecimal`.
 */
export function parsePriceToMinor(
  raw: string,
  currency: string
): number | null {
  const major = parseTypedDecimal(raw, currencyFractionDigits(currency));
  return major === null ? null : majorToMinor(major, currency);
}

/**
 * What a price field holds. An entry that is not an amount of 0 or more is
 * unreadable: it is refused, never saved as an empty field.
 */
export type PriceEntry =
  | { kind: "empty" }
  | { kind: "amount"; minor: number }
  | { kind: "unreadable" };

export function readPriceEntry(raw: string, currency: string): PriceEntry {
  if (raw.trim() === "") {
    return { kind: "empty" };
  }
  const minor = parsePriceToMinor(raw, currency);
  return minor === null ? { kind: "unreadable" } : { kind: "amount", minor };
}

const EXAMPLE_PRICE_MAJOR = 89.5;

/** A price to show a host as one they could type, in the currency's decimals. */
export function examplePrice(currency: string): number {
  return minorToMajor(majorToMinor(EXAMPLE_PRICE_MAJOR, currency), currency);
}

/**
 * Format an integer minor-unit amount as a localized currency string
 * (e.g. 125000 + "EUR" -> "€1,250.00"). Locale defaults to the runtime's.
 * Tolerates a null/empty currency (falls back to the default) and an unknown
 * code (plain "<amount> <CODE>").
 */
export function formatMinor(
  amountMinor: number,
  currency: string | null | undefined,
  locale?: string
): string {
  const code = (currency || DEFAULT_CURRENCY).toUpperCase();
  const value = minorToMajor(amountMinor, code);
  try {
    return new Intl.NumberFormat(locale, {
      currency: code,
      style: "currency",
    }).format(value);
  } catch {
    return `${value.toFixed(currencyFractionDigits(code))} ${code}`.trim();
  }
}

/** Format a {@link Money} value. See {@link formatMinor}. */
export function formatMoney(money: Money, locale?: string): string {
  return formatMinor(money.amountMinor, money.currency, locale);
}

/**
 * Best-effort default currency for an ISO-3166-1 alpha-2 country code, used to
 * seed a new property's currency from its address. A host can always override
 * it. Sourced from country-to-currency (full ISO-3166 coverage); an unmapped or
 * null country falls back to the default rather than guessing.
 */
export function currencyForCountry(
  countryCode: string | null | undefined
): CurrencyCode {
  if (!countryCode) {
    return DEFAULT_CURRENCY;
  }
  // The library types its keys as a country-code union; index it as a plain
  // record so arbitrary input falls back cleanly on anything unknown.
  const byCountry = countryToCurrency as Record<string, string | undefined>;
  return byCountry[countryCode.toUpperCase()] ?? DEFAULT_CURRENCY;
}
